import { FC, useMemo } from 'react';
import { Card, Heading } from '@qga/roo-ui/components';
import styled from '@emotion/styled';
import { themeGet } from '@styled-system/theme-get';
import { sanitizeHTML } from 'lib/utils';
import { DetailsBooking } from 'types';
import { BreakOnNewLine, ContentHeading } from 'components/Typography';
import CollapsibleCard from 'components/CollapsibleCard';
import MessageBoard from 'components/MessageBoard';
import areEmpty from './utils/areEmpty';
import isAbove400CharactersLimit from './utils/isAbove400CharactersLimit';
import { TravelDocumentationReminder } from '../../../Alerts/Inline/TravelDocumentationReminder';

const MaxWidthContent = styled.div`
  > :first-of-type {
    margin-top: 0;
  }

  ${themeGet('mediaQueries.0')} {
    max-width: 66.66%;
  }
`;

interface KnowBeforeYouGoProps {
  booking: DetailsBooking;
  onToggle: (isOpen: boolean) => void;
}

const KnowBeforeYouGo: FC<KnowBeforeYouGoProps> = ({ booking, onToggle }) => {
  const { checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription } = useMemo(
    () => ({
      checkInInstructions: sanitizeHTML(booking.reservation.property.checkInInstructions),
      knowBeforeYouGoDescription: sanitizeHTML(booking.reservation.property.knowBeforeYouGoDescription),
      mandatoryFeesDescription: sanitizeHTML(booking.reservation.property.mandatoryFeesDescription),
    }),
    [booking],
  );

  const showInstructionAndDescription = !areEmpty(checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription);
  const showTravelDocumentationReminder = !booking.reservation.isAfterCheckOut && !booking.flags.isCancelled;

  if (
    booking.reservation.isAfterCheckOut ||
    !booking.flags.isBooked ||
    (!showInstructionAndDescription && !showTravelDocumentationReminder)
  ) {
    return null;
  }

  const showHotelPolicies = !areEmpty(knowBeforeYouGoDescription, mandatoryFeesDescription);
  const shouldCollapse = isAbove400CharactersLimit(checkInInstructions, knowBeforeYouGoDescription, mandatoryFeesDescription);

  return (
    <>
      <ContentHeading>Know before you go</ContentHeading>

      {showInstructionAndDescription && (
        <>
          <MessageBoard mb="5" mt="-8px">
            <strong>Please note,</strong> there is important information to know before checking in.
          </MessageBoard>

          <Card p={0} mb={showTravelDocumentationReminder ? 5 : 8}>
            <CollapsibleCard onToggle={onToggle} collapsible={shouldCollapse}>
              <MaxWidthContent>
                {checkInInstructions && (
                  <>
                    <Heading.h4 mt={6} data-testid="check-in-instructions-heading">
                      Check-In Instructions
                    </Heading.h4>
                    <BreakOnNewLine dangerouslySetInnerHTML={{ __html: checkInInstructions }} />
                  </>
                )}
                {showHotelPolicies && (
                  <Heading.h4 mt={6} data-testid="hotel-policies-heading">
                    Hotel Policies
                  </Heading.h4>
                )}
                {knowBeforeYouGoDescription && (
                  <>
                    <Heading.h5 data-testid="know-before-you-go-heading">Know Before You Go</Heading.h5>
                    <BreakOnNewLine dangerouslySetInnerHTML={{ __html: knowBeforeYouGoDescription }} />
                  </>
                )}
                {mandatoryFeesDescription && (
                  <>
                    <Heading.h5 data-testid="mandatory-fees-heading">Fees</Heading.h5>
                    <BreakOnNewLine dangerouslySetInnerHTML={{ __html: mandatoryFeesDescription }} />
                  </>
                )}
              </MaxWidthContent>
            </CollapsibleCard>
          </Card>
        </>
      )}

      {showTravelDocumentationReminder && <TravelDocumentationReminder />}
    </>
  );
};

export default KnowBeforeYouGo;
